import { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "../../../../i18n/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCog,
  faInfoCircle,
  faBrain,
  faClock,
  faEnvelope,
  faComments,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import HelpGuideButton from "../../HelpGuideButton";
import SophisticationLevel from "../components/SophisticationLevel";
import ContactFrequency from "../components/ContactFrequency";
import CommunicationChannel from "../components/CommunicationChannel";
import CommunicationTime from "../components/CommunicationTime";
import CommunicationContext from "../components/CommunicationContext";
import SimulationProfileAPI from "../../services/SimulationProfileAPI";
import SimulationScheduleAPI from "../../services/SimulationScheduleAPI";
import UserAPI from "../../services/UserAPI";
import { getCookie } from "../../utils/CookieUtil";
import { useUser } from "../../context/UserContext";
import { useSubscription } from "../../context/SubscriptionContext";
import { useAccountSetup } from "../../context/AccountSetupContext";
import { useRoleCheck } from "../../../../hooks/useRoleCheck";

const useSimulationProfileState = () => {
  const [activeSection, setActiveSection] = useState("profile"); // Start with profile overview
  const [uuid, setUuid] = useState("0");
  const { t } = useTranslation();
  const [profileName, setProfileName] = useState("");

  const [sophistication, setSophistication] = useState(4);
  const [frequency, setFrequency] = useState("5");
  const [communicationChannel, setCommunicationChannel] = useState([
    "email",
    "sms",
  ]);
  const [timeOfCommunication, setTimeOfCommunication] = useState("Day time");
  const [communicationContext, setCommunicationContext] = useState([
    "general",
    "regional",
    "events",
  ]);
  const [selectedPreset, setSelectedPreset] = useState("Average");
  const [isEditable, setIsEditable] = useState(false);

  // Map internal preset IDs to user-friendly display names
  const presetDisplayNames = {
    Easy: "Gentle",
    Average: "Moderate",
    Difficult: "Challenging",
    Custom: "Custom",
  };
  const presetDisplayName =
    presetDisplayNames[selectedPreset] || selectedPreset;
  const [existingProfile, setExistingProfile] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Pause feature state
  const [isPaused, setIsPaused] = useState(false);
  const [hasActiveSchedule, setHasActiveSchedule] = useState(true);
  const [profileStatus, setProfileStatus] = useState(null); // "ACTIVE" or "PAUSED" from backend
  const [pauseStartDate, setPauseStartDate] = useState(null);
  const [pauseEndDate, setPauseEndDate] = useState(null);
  const [pauseDurationDays, setPauseDurationDays] = useState(null);
  const [pauseReason, setPauseReason] = useState("");
  const [pausedAt, setPausedAt] = useState(null);
  const [pausedBy, setPausedBy] = useState(null);
  const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);

  // State to track original values for change detection
  const [originalValues, setOriginalValues] = useState({
    selectedPreset: "Average",
    sophistication: 4,
    frequency: "5",
    communicationChannel: ["email", "sms"],
    timeOfCommunication: "Day time",
    communicationContext: ["general", "regional", "events"],
  });

  // Get selected user from UserContext
  const { selectedUser, loggedInUser } = useUser();

  // Check subscription for Custom profile access
  // Note: Subscription is tied to the logged-in user (account owner), not the selected user
  const {
    subscription,
    servicePlan,
    hasFeature,
    isLoading: subscriptionLoading,
    error: subscriptionError,
  } = useSubscription();

  // Account setup progress tracking
  const { refreshSetupProgress } = useAccountSetup();

  // Check user roles for UI display permissions
  const { isGroupAdmin } = useRoleCheck();

  // Memoize the custom profile access result to avoid redundant calls
  const canAccessCustomProfile = useMemo(() => {
    if (subscriptionLoading) return false;

    // Custom profiles are available for family plan or higher
    // Check if the service plan has custom training enabled (which indicates family or higher)
    if (servicePlan?.hasCustomTraining) {
      return true;
    }

    // Fallback: Check plan name directly
    const planName = servicePlan?.planName?.toLowerCase();
    return (
      planName === "family" ||
      planName === "extended_family" ||
      servicePlan?.tier >= 2
    );
  }, [servicePlan, subscriptionLoading]);

  // Debug logging
  useEffect(() => {
    console.log("SimulationProfileSettings Debug:");
    console.log("- selectedUser:", selectedUser);
    console.log("- loggedInUser:", loggedInUser);
    console.log("- servicePlan:", servicePlan);
    console.log("- canAccessCustomProfile result:", canAccessCustomProfile);
    console.log("- subscriptionLoading:", subscriptionLoading);
    console.log("- subscriptionError:", subscriptionError);
    console.log("- selectedPreset:", selectedPreset);
  }, [
    selectedUser,
    loggedInUser,
    servicePlan,
    subscriptionLoading,
    subscriptionError,
    selectedPreset,
    canAccessCustomProfile,
  ]);

  // Define sections for the sidebar navigation
  const sections = [
    {
      id: "profile",
      title: "Profile Overview",
      icon: faCog,
      description: "Configure preset settings and view configuration summary",
      disabled: false, // Profile overview is always accessible
    },
    {
      id: "sophistication",
      title: "Sophistication Level",
      icon: faBrain,
      description:
        "Set the complexity and sophistication of simulation scenarios",
      disabled: !isEditable, // Disabled if not using Custom preset
    },
    {
      id: "frequency",
      title: "Contact Frequency",
      icon: faClock,
      description: "Configure how often simulation messages are sent",
      disabled: !isEditable, // Disabled if not using Custom preset
    },
    {
      id: "channels",
      title: "Communication Channels",
      icon: faEnvelope,
      description: "Select which communication methods to use for simulations",
      disabled: !isEditable, // Disabled if not using Custom preset
    },
    {
      id: "timing",
      title: "Communication Time",
      icon: faCalendarAlt,
      description: "Set preferred timing for when simulations are delivered",
      disabled: !isEditable, // Disabled if not using Custom preset
    },
    {
      id: "context",
      title: "Communication Context",
      icon: faComments,
      description: "Choose the types of scenarios and contexts for simulations",
      disabled: !isEditable, // Disabled if not using Custom preset
    },
  ];

  // Preset configurations
  const presets = {
    Easy: {
      sophistication: 1,
      frequency: "2",
      communicationChannel: ["email"],
      timeOfCommunication: "Day time",
      communicationContext: ["general", "regional"],
    },
    Average: {
      sophistication: 4,
      frequency: "5",
      communicationChannel: ["email", "sms"],
      timeOfCommunication: "Day time",
      communicationContext: ["general", "regional", "events"],
    },
    Difficult: {
      sophistication: 7,
      frequency: "8",
      communicationChannel: ["email", "sms", "voice"],
      timeOfCommunication: "Any time",
      communicationContext: [
        "general",
        "regional",
        "events",
        "financial",
        "emergencies",
        "personalized",
      ],
    },
  };

  // Load existing profile data
  useEffect(() => {
    const fetchExistingProfile = async () => {
      if (!selectedUser) return;

      try {
        setLoading(true);
        const profile = await SimulationProfileAPI.getSimulationProfilesByUuid(
          selectedUser.uuid,
        );
        console.log(
          "Fetched user profile for UUID:",
          selectedUser.uuid,
          profile,
        );

        if (profile) {
          // Profile exists - it's a single object, not an array
          setExistingProfile([profile]); // Convert to array for consistency with existing state structure
          setUuid(profile.uuid || selectedUser.uuid);
          setProfileName(profile.profileName || selectedPreset);
          setSophistication(profile.sophistication || 0);
          setFrequency(profile.frequency || "5");
          setCommunicationChannel(profile.communicationChannel || []);
          setTimeOfCommunication(profile.timeOfCommunication || "Day time");
          setCommunicationContext(profile.communicationContext || []);

          // Track the profile-level status (ACTIVE or PAUSED)
          setProfileStatus(profile.status || null);

          // Set preset based on profile name
          const detectedPreset = profile.profileName || "Average";
          setSelectedPreset(detectedPreset);
          setIsEditable(detectedPreset === "Custom");

          // Set original values for change detection
          setOriginalValues({
            selectedPreset: detectedPreset,
            sophistication: profile.sophistication || 0,
            frequency: profile.frequency || "5",
            communicationChannel: profile.communicationChannel || [],
            timeOfCommunication: profile.timeOfCommunication || "Day time",
            communicationContext: profile.communicationContext || [],
          });
        } else {
          setExistingProfile([]);
          // Set uuid from selectedUser when no existing profile
          setUuid(selectedUser.uuid);

          // Set original values for new profile (defaults to Average/Moderate)
          setOriginalValues({
            selectedPreset: "Average",
            sophistication: 4,
            frequency: "5",
            communicationChannel: ["email", "sms"],
            timeOfCommunication: "Day time",
            communicationContext: ["general", "regional", "events"],
          });
        }
      } catch (error) {
        console.error("Error fetching simulation profile:", error);
        setLoadError("Failed to load existing profile data");
      } finally {
        setLoading(false);
      }
    };

    fetchExistingProfile();
  }, [selectedUser]);

  // Load pause status when user changes
  useEffect(() => {
    const fetchPauseStatus = async () => {
      if (!selectedUser?.uuid) return;

      try {
        const pauseStatus = await SimulationScheduleAPI.getPauseStatus(
          selectedUser.uuid,
        );

        // Track whether the user has an active schedule
        const activeSchedule = pauseStatus?.hasActiveSchedule !== false;
        setHasActiveSchedule(activeSchedule);

        if (pauseStatus && pauseStatus.isPaused) {
          setIsPaused(pauseStatus.isPaused);
          setPauseStartDate(pauseStatus.pauseStartDate);
          setPauseEndDate(pauseStatus.pauseEndDate);
          setPauseDurationDays(pauseStatus.pauseDurationDays);
          setPauseReason(pauseStatus.pauseReason || "");
          setPausedAt(pauseStatus.pausedAt);
          setPausedBy(pauseStatus.pausedBy);
        } else {
          // Reset pause state if no pause data
          setIsPaused(false);
          setPauseStartDate(null);
          setPauseEndDate(null);
          setPauseDurationDays(null);
          setPauseReason("");
          setPausedAt(null);
          setPausedBy(null);
        }
      } catch (error) {
        console.error("Error fetching pause status:", error);
        // Don't show error to user, just keep pause as inactive
        setIsPaused(false);
        setHasActiveSchedule(false);
      }
    };

    fetchPauseStatus();
  }, [selectedUser]);

  // Handle preset selection change
  const handlePresetChange = (event) => {
    const preset = event.target.value;
    setSelectedPreset(preset);

    if (preset === "Custom") {
      setIsEditable(true);
    } else {
      setIsEditable(false);
      const presetConfig = presets[preset];
      if (presetConfig) {
        setSophistication(presetConfig.sophistication);
        setFrequency(presetConfig.frequency);
        setCommunicationChannel(presetConfig.communicationChannel);
        setTimeOfCommunication(presetConfig.timeOfCommunication);
        setCommunicationContext(presetConfig.communicationContext);
      }
    }
  };

  // Monitor subscription status and adjust preset if needed
  useEffect(() => {
    // If user selected Custom but doesn't have access, downgrade to Average (Moderate)
    if (
      selectedPreset === "Custom" &&
      !subscriptionLoading &&
      !canAccessCustomProfile
    ) {
      console.log(
        "User doesn't have premium subscription, switching from Custom to Average (Moderate) profile",
      );
      setSelectedPreset("Average");
      setIsEditable(false);
      setSophistication(presets["Average"].sophistication);
      setFrequency(presets["Average"].frequency);
      setCommunicationChannel(presets["Average"].communicationChannel);
      setTimeOfCommunication(presets["Average"].timeOfCommunication);
      setCommunicationContext(presets["Average"].communicationContext);
    }
  }, [selectedPreset, canAccessCustomProfile, subscriptionLoading]);

  // Handle form submission
  const handleSubmit = async () => {
    const profileData = {
      uuid: uuid,
      profileName: selectedPreset, // Save the preset value as the profile name
      sophistication: sophistication,
      frequency: frequency,
      communicationChannel: communicationChannel,
      timeOfCommunication: timeOfCommunication,
      communicationContext: communicationContext,
    };

    console.log("=== DEBUGGING MOBILE SAVE ===");
    console.log("Profile data being saved:", profileData);
    console.log("Communication context state:", communicationContext);
    console.log("Is editable:", isEditable);
    console.log("Selected preset:", selectedPreset);
    console.log("================================");

    try {
      const response =
        await SimulationProfileAPI.createOrUpdateSimulationProfile(profileData);
      if (response) {
        toast.success(t("simProfile.toast.profileSaved"));
        console.log("Save response:", response);

        // Refresh account setup progress so the dashboard journey step updates
        if (refreshSetupProgress) {
          await refreshSetupProgress();
        }

        // Update original values to current values after successful save
        setOriginalValues({
          selectedPreset: selectedPreset,
          sophistication: sophistication,
          frequency: frequency,
          communicationChannel: [...communicationChannel],
          timeOfCommunication: timeOfCommunication,
          communicationContext: [...communicationContext],
        });
      } else {
        toast.error(t("simProfile.toast.profileSaveFailed"));
      }
    } catch (error) {
      console.error("Error saving simulation profile:", error);
      toast.error(t("simProfile.toast.profileSaveError"));
    }
  };

  // Helper to capitalize first letter of each word
  const capitalize = (str) => str.replace(/\b\w/g, (c) => c.toUpperCase());

  // Render section content based on active section
  const renderSectionContent = () => {
    switch (activeSection) {
      case "profile":
        return (
          <div>
            {/* Configuration Summary */}
            <div className="common-form-group" style={{ marginTop: "0rem" }}>
              <h4>Current Configuration Summary</h4>
              <div className="config-summary">
                <div className="summary-item">
                  <strong>Sophistication Level:</strong>
                  <span
                    className={`summary-badge summary-badge-level ${
                      sophistication >= 8
                        ? "level-high"
                        : sophistication >= 5
                          ? "level-medium"
                          : "level-low"
                    }`}
                  >
                    {sophistication}/10
                  </span>
                </div>
                <div className="summary-item">
                  <strong>Contact Frequency:</strong>
                  <span className="summary-badge summary-badge-frequency">
                    {frequency}× per 10-day period
                  </span>
                </div>
                <div className="summary-item">
                  <strong>Communication Channels:</strong>
                  <span className="summary-badges">
                    {communicationChannel.length > 0 ? (
                      communicationChannel.map((ch) => (
                        <span
                          key={ch}
                          className="summary-badge summary-badge-channel"
                        >
                          {capitalize(ch)}
                        </span>
                      ))
                    ) : (
                      <span className="summary-badge summary-badge-none">
                        None selected
                      </span>
                    )}
                  </span>
                </div>
                <div className="summary-item">
                  <strong>Communication Time:</strong>
                  <span className="summary-badge summary-badge-time">
                    {capitalize(timeOfCommunication)}
                  </span>
                </div>
                <div className="summary-item">
                  <strong>Communication Context:</strong>
                  <span className="summary-badges">
                    {communicationContext.length > 0 ? (
                      communicationContext.map((ctx) => (
                        <span
                          key={ctx}
                          className="summary-badge summary-badge-context"
                        >
                          {capitalize(ctx)}
                        </span>
                      ))
                    ) : (
                      <span className="summary-badge summary-badge-none">
                        None selected
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );

      case "sophistication":
        return (
          <div>
            <div className="common-form-group">
              <p className="simulation-profile-section-description">
                Set the complexity and sophistication of simulation scenarios.
                Higher sophistication levels include more subtle social
                engineering tactics and advanced psychological manipulation
                techniques.
              </p>
            </div>
            <div className="component-container">
              <div
                className={`control-wrapper ${!isEditable ? "disabled" : ""}`}
              >
                <SophisticationLevel
                  value={sophistication}
                  onChange={isEditable ? setSophistication : () => {}}
                />
              </div>
            </div>
            {!isEditable && (
              <div className="common-message info">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>
                  This setting is controlled by the "{presetDisplayName}"
                  preset. Switch to Custom preset to modify.
                </span>
              </div>
            )}
          </div>
        );

      case "frequency":
        return (
          <div>
            <div className="common-form-group">
              <p className="simulation-profile-section-description">
                Configure how often simulation messages are sent to the user.
                Higher frequencies provide more training opportunities but may
                feel overwhelming.
              </p>
            </div>
            <div className="component-container">
              <div
                className={`control-wrapper ${!isEditable ? "disabled" : ""}`}
              >
                <ContactFrequency
                  value={frequency}
                  onChange={isEditable ? setFrequency : () => {}}
                />
              </div>
            </div>
            {!isEditable && (
              <div className="common-message info">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>
                  This setting is controlled by the "{presetDisplayName}"
                  preset. Switch to Custom preset to modify.
                </span>
              </div>
            )}
          </div>
        );

      case "channels":
        return (
          <div>
            <div className="common-form-group">
              <p className="simulation-profile-section-description">
                Select which communication methods to use for simulations. Each
                channel provides different training scenarios and user
                experiences.
              </p>
            </div>
            <div className="component-container">
              <div
                className={`control-wrapper ${!isEditable ? "disabled" : ""}`}
              >
                <CommunicationChannel
                  value={communicationChannel}
                  onChange={isEditable ? setCommunicationChannel : () => {}}
                />
              </div>
            </div>
            {!isEditable && (
              <div className="common-message info">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>
                  This setting is controlled by the "{presetDisplayName}"
                  preset. Switch to Custom preset to modify.
                </span>
              </div>
            )}
          </div>
        );

      case "context":
        return (
          <div>
            <div className="common-form-group">
              <p className="simulation-profile-section-description">
                Choose the types of scenarios and contexts for simulations. This
                determines the themes and situations used in training messages.
              </p>
            </div>
            <div className="component-container">
              <div
                className={`control-wrapper ${!isEditable ? "disabled" : ""}`}
              >
                <CommunicationContext
                  value={communicationContext}
                  onChange={isEditable ? setCommunicationContext : () => {}}
                />
              </div>
            </div>
            {!isEditable && (
              <div className="common-message info">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>
                  This setting is controlled by the "{presetDisplayName}"
                  preset. Switch to Custom preset to modify.
                </span>
              </div>
            )}
          </div>
        );

      case "timing":
        return (
          <div>
            <div className="common-form-group">
              <p className="simulation-profile-section-description">
                Set the preferred timing for when simulation messages are
                delivered. This helps ensure messages arrive when users are most
                likely to engage with them.
              </p>
            </div>
            <div className="component-container">
              <div
                className={`control-wrapper ${!isEditable ? "disabled" : ""}`}
              >
                <CommunicationTime
                  value={timeOfCommunication}
                  onChange={isEditable ? setTimeOfCommunication : () => {}}
                />
              </div>
            </div>
            {!isEditable && (
              <div className="common-message info">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>
                  This setting is controlled by the "{presetDisplayName}"
                  preset. Switch to Custom preset to modify.
                </span>
              </div>
            )}
          </div>
        );

      default:
        return <div>Section not found</div>;
    }
  };

  // Render the profile preset section (used by both desktop and mobile)
  const renderProfileSection = () => {
    return (
      <div className="profile-preset-section">
        <div className="common-form-group">
          <div className="common-form-field">
            <label
              htmlFor="presetSelection"
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              Choose a Simulation Profile &nbsp;&nbsp;&nbsp;
              <HelpGuideButton guideId={14} />
            </label>
            <select
              id="presetSelection"
              className="common-select"
              value={selectedPreset}
              onChange={handlePresetChange}
            >
              <option value="Easy">Gentle - Beginner Level</option>
              <option value="Average">Moderate - Intermediate Level</option>
              <option value="Difficult">Challenging - Advanced Level</option>
              <option
                value="Custom"
                disabled={!canAccessCustomProfile}
                title={
                  !canAccessCustomProfile
                    ? "Custom profiles require a Family subscription"
                    : ""
                }
              >
                {canAccessCustomProfile
                  ? "Custom - Manual Configuration"
                  : "Custom - Manual Configuration (Requires Family Subscription)"}
              </option>
            </select>

            {!canAccessCustomProfile && selectedPreset === "Custom" && (
              <div className="common-message warning">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>Custom profiles require a Family subscription plan.</span>
              </div>
            )}

            {!canAccessCustomProfile && (
              <div className="subscription-note">
                Custom profile requires Family subscription
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Handle save profile action (alias for handleSubmit)
  const handleSaveProfile = handleSubmit;

  // Pause handler functions
  const handlePause = () => {
    if (!hasActiveSchedule) {
      toast("This user does not have an active simulation schedule to pause.", {
        icon: "ℹ️",
        duration: 4000,
        position: "top-center",
      });
      return;
    }
    setIsPauseModalOpen(true);
  };

  const handleResume = async () => {
    if (!selectedUser?.uuid) {
      toast.error(t("simProfile.toast.noUserSelected"));
      return;
    }

    if (!hasActiveSchedule) {
      toast(
        "This user does not have an active simulation schedule to resume.",
        {
          icon: "ℹ️",
          duration: 4000,
          position: "top-center",
        },
      );
      return;
    }

    try {
      const response = await SimulationScheduleAPI.resumeSchedule(
        selectedUser.uuid,
      );

      // Backend returns hasActiveSchedule: false when no schedule exists
      if (response && response.hasActiveSchedule === false) {
        setHasActiveSchedule(false);
        toast(
          "This user does not have an active simulation schedule to resume.",
          {
            icon: "ℹ️",
            duration: 4000,
            position: "top-center",
          },
        );
        return;
      }

      setIsPaused(false);
      setPauseStartDate(null);
      setPauseEndDate(null);
      setPauseDurationDays(null);
      setPauseReason("");
      setPausedAt(null);
      setPausedBy(null);

      toast.success(t("simProfile.toast.scheduleResumed"), {
        duration: 4000,
        position: "top-center",
      });
    } catch (error) {
      console.error("Error resuming schedule:", error);
      toast.error(t("simProfile.toast.resumeFailed"));
    }
  };

  const handlePauseConfirm = async (pauseData) => {
    if (!selectedUser?.uuid) {
      toast.error(t("simProfile.toast.noUserSelected"));
      return;
    }

    try {
      const response = await SimulationScheduleAPI.pauseSchedule(
        selectedUser.uuid,
        pauseData,
      );

      // Backend returns hasActiveSchedule: false when no schedule exists
      if (response && response.hasActiveSchedule === false) {
        setHasActiveSchedule(false);
        setIsPauseModalOpen(false);
        toast(
          "This user does not have an active simulation schedule to pause.",
          {
            icon: "ℹ️",
            duration: 4000,
            position: "top-center",
          },
        );
        return;
      }

      const now = new Date();
      setIsPaused(true);
      setPauseStartDate(now.toISOString());
      setPauseReason(pauseData.reason);
      setPausedAt(now.toISOString());
      setPausedBy(selectedUser?.uuid || "current-user");

      if (pauseData.pauseOption === "indefinite") {
        setPauseEndDate(null);
        setPauseDurationDays(null);
      } else if (pauseData.pauseOption === "until-date") {
        setPauseEndDate(pauseData.endDate);
        setPauseDurationDays(null);
      } else if (pauseData.pauseOption === "duration") {
        setPauseDurationDays(pauseData.durationDays);
        const endDate = new Date(now);
        endDate.setDate(endDate.getDate() + parseInt(pauseData.durationDays));
        setPauseEndDate(endDate.toISOString());
      }

      setIsPauseModalOpen(false);

      toast.success(t("simProfile.toast.schedulePaused"), {
        duration: 4000,
        position: "top-center",
      });
    } catch (error) {
      console.error("Error pausing schedule:", error);
      toast.error(t("simProfile.toast.pauseFailed"), {
        duration: 4000,
        position: "top-center",
      });
    }
  };

  // Render the schedule section
  const renderScheduleSection = () => {
    return null; // ScheduleControl component will be rendered in the main component
  };

  // Check if any values have changed from the original
  const hasChanges = () => {
    const arraysEqual = (a, b) => {
      if (a.length !== b.length) return false;
      return a.every((val, index) => val === b[index]);
    };

    return (
      selectedPreset !== originalValues.selectedPreset ||
      sophistication !== originalValues.sophistication ||
      frequency !== originalValues.frequency ||
      !arraysEqual(communicationChannel, originalValues.communicationChannel) ||
      timeOfCommunication !== originalValues.timeOfCommunication ||
      !arraysEqual(communicationContext, originalValues.communicationContext)
    );
  };

  // Reset all values back to the last saved state
  const resetToOriginal = () => {
    setSelectedPreset(originalValues.selectedPreset);
    setIsEditable(originalValues.selectedPreset === "Custom");
    setSophistication(originalValues.sophistication);
    setFrequency(originalValues.frequency);
    setCommunicationChannel([...originalValues.communicationChannel]);
    setTimeOfCommunication(originalValues.timeOfCommunication);
    setCommunicationContext([...originalValues.communicationContext]);
  };

  // Validate if profile is ready to be saved
  const isProfileValid = () => {
    return selectedUser && uuid && selectedPreset && hasChanges();
  };

  return {
    // State
    activeSection,
    setActiveSection,
    uuid,
    setUuid,
    profileName,
    setProfileName,
    sophistication,
    setSophistication,
    frequency,
    setFrequency,
    communicationChannel,
    setCommunicationChannel,
    timeOfCommunication,
    setTimeOfCommunication,
    communicationContext,
    setCommunicationContext,
    selectedPreset,
    setSelectedPreset,
    isEditable,
    setIsEditable,
    existingProfile,
    setExistingProfile,
    loading,
    setLoading,
    loadError,
    setLoadError,

    // Context values
    selectedUser,
    loggedInUser,
    canAccessCustomProfile,
    subscriptionLoading,
    servicePlan,
    subscriptionError,
    isGroupAdmin,

    // Pause state
    isPaused,
    hasActiveSchedule,
    profileStatus,
    pauseStartDate,
    pauseEndDate,
    pauseDurationDays,
    pauseReason,
    pausedAt,
    pausedBy,
    isPauseModalOpen,
    setIsPauseModalOpen,

    // Computed values
    sections,
    presets,
    presetDisplayName,
    presetDisplayNames,

    // Handlers
    handlePresetChange,
    handleSubmit,
    handleSaveProfile,
    resetToOriginal,
    handlePause,
    handleResume,
    handlePauseConfirm,
    renderSectionContent,
    renderProfileSection,
    renderScheduleSection,

    // Validation
    isProfileValid,
    hasChanges,
  };
};

export { useSimulationProfileState };
